/**
 * PDF de inventario: resumen de aprobación y listado por cliente/proyecto.
 */
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';

const PURPLE = [94, 53, 177];
const INK = [28, 29, 33];
const MUTED = [95, 99, 104];
const LINE = [232, 234, 237];
const WASH = [244, 245, 247];
const GREEN = [19, 115, 51];
const ORANGE = [227, 116, 0];
const RED = [197, 34, 31];

function ensurePage(doc, y, need = 16) {
    const bottom = doc.internal.pageSize.getHeight() - 14;
    if (y + need <= bottom) return y;
    doc.addPage();
    return 16;
}

function drawHeader(doc, { generatedAt, scopeLabel }) {
    const pageW = doc.internal.pageSize.getWidth();
    doc.setFillColor(...PURPLE);
    doc.rect(0, 0, pageW, 22, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Inventario de reportes', 14, 10);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Portal de Reportes · Central Media  ·  ${generatedAt}`, 14, 16);
    if (scopeLabel) {
        doc.setTextColor(...MUTED);
        doc.setFontSize(8);
        doc.text(scopeLabel, 14, 28);
        return 34;
    }
    return 30;
}

function drawFooter(doc) {
    const pages = doc.getNumberOfPages();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    for (let i = 1; i <= pages; i += 1) {
        doc.setPage(i);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...MUTED);
        doc.text(`Página ${i} de ${pages}`, pageW - 14, pageH - 7, { align: 'right' });
    }
}

function statBox(doc, x, y, w, label, value, color = INK) {
    doc.setFillColor(...WASH);
    doc.setDrawColor(...LINE);
    doc.roundedRect(x, y, w, 16, 2, 2, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...color);
    doc.text(String(value), x + 4, y + 7);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...MUTED);
    doc.text(label, x + 4, y + 12);
}

function statusColor(status) {
    if (status === 'complete') return GREEN;
    if (status === 'complete_late') return ORANGE;
    if (status === 'overdue') return RED;
    if (status === 'pending') return ORANGE;
    return MUTED;
}

export function exportInventoryPdf({
    groups,
    stats,
    scopeLabel,
    filename,
}) {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4', compress: true });
    const pageW = doc.internal.pageSize.getWidth();
    const generatedAt = new Date().toLocaleString('es-MX', { dateStyle: 'full', timeStyle: 'short' });
    let y = drawHeader(doc, { generatedAt, scopeLabel });

    const boxW = (pageW - 28 - 20) / 5;
    const pct = stats.pct == null ? '—' : `${stats.pct}%`;
    statBox(doc, 14, y, boxW, 'Reportes', stats.reports);
    statBox(doc, 14 + boxW + 5, y, boxW, 'Aprobación global', pct, stats.pct == null ? MUTED : (stats.pct >= 100 ? GREEN : stats.pct >= 50 ? ORANGE : RED));
    statBox(doc, 14 + (boxW + 5) * 2, y, boxW, 'Completos', stats.complete, GREEN);
    statBox(doc, 14 + (boxW + 5) * 3, y, boxW, 'En curso', stats.pending, ORANGE);
    statBox(doc, 14 + (boxW + 5) * 4, y, boxW, 'Vencidos', stats.overdue, RED);
    y += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
        `Marcas de aprobación: ${stats.approvedMarks} de ${stats.totalMarks || 0}. Sin responsables: ${stats.none}. Aprobados con retrasos: ${stats.complete_late || 0}.`,
        14,
        y
    );
    y += 8;

    const cols = [
        { key: 'project', label: 'Proyecto', w: 42 },
        { key: 'name', label: 'Reporte', w: 78 },
        { key: 'type', label: 'Tipo', w: 26 },
        { key: 'approval', label: 'Aprobación', w: 28 },
        { key: 'pct', label: '%', w: 18 },
        { key: 'status', label: 'Estado', w: 28 },
        { key: 'deadline', label: 'Deadline', w: 32 },
    ];
    const tableW = cols.reduce((sum, col) => sum + col.w, 0);

    const drawTableHead = (top) => {
        doc.setFillColor(...PURPLE);
        doc.rect(14, top, tableW, 7, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(255, 255, 255);
        let x = 14;
        cols.forEach((col) => {
            doc.text(col.label, x + 2, top + 5);
            x += col.w;
        });
        return top + 7;
    };

    groups.forEach((group) => {
        y = ensurePage(doc, y, 24);
        doc.setFillColor(244, 238, 252);
        doc.rect(14, y, tableW, 8, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...PURPLE);
        const clientPct = group.stats.pct == null ? 'sin responsables' : `${group.stats.pct}% aprobación`;
        doc.text(
            `${group.client}   ·   ${group.count} reportes   ·   ${clientPct}   ·   ${group.stats.complete} completos / ${group.stats.complete_late || 0} con retrasos / ${group.stats.pending} en curso / ${group.stats.overdue} vencidos`,
            16,
            y + 5.5
        );
        y += 10;

        group.projects.forEach((project) => {
            y = ensurePage(doc, y, 20);
            y = drawTableHead(y);
            project.reports.forEach((row, index) => {
                y = ensurePage(doc, y, 8);
                if (index % 2 === 1) {
                    doc.setFillColor(...WASH);
                    doc.rect(14, y, tableW, 7, 'F');
                }
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(7.5);
                doc.setTextColor(...INK);
                let x = 14;
                const cells = [
                    index === 0 ? project.project : '',
                    row.name,
                    row.type,
                    row.approval,
                    row.pct,
                    row.status,
                    row.deadline,
                ];
                cells.forEach((cell, colIndex) => {
                    const col = cols[colIndex];
                    const text = doc.splitTextToSize(String(cell || ''), col.w - 3)[0] || '';
                    if (col.key === 'status') doc.setTextColor(...statusColor(row.statusKey));
                    else doc.setTextColor(...INK);
                    doc.text(text, x + 2, y + 5);
                    x += col.w;
                });
                y += 7;
            });
            y += 3;
        });
        y += 2;
    });

    drawFooter(doc);
    doc.save(filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

export function exportInventoryExcel({ groups, stats, scopeLabel, filename }) {
    const wb = XLSX.utils.book_new();
    const resumen = [
        ['Inventario de reportes'],
        [scopeLabel || ''],
        [],
        ['Métrica', 'Valor'],
        ['Reportes', stats.reports],
        ['Aprobación global (%)', stats.pct == null ? '' : stats.pct],
        ['Completos', stats.complete],
        ['Aprobados con retrasos', stats.complete_late || 0],
        ['En curso', stats.pending],
        ['Vencidos', stats.overdue],
        ['Sin responsables', stats.none],
        ['Marcas aprobadas', stats.approvedMarks],
        ['Marcas totales', stats.totalMarks],
        [],
        ['Cliente', 'Reportes', 'Aprobación %', 'Completos', 'Con retrasos', 'En curso', 'Vencidos', 'Sin responsables'],
        ...groups.map((group) => [
            group.client,
            group.count,
            group.stats.pct == null ? '' : group.stats.pct,
            group.stats.complete,
            group.stats.complete_late || 0,
            group.stats.pending,
            group.stats.overdue,
            group.stats.none,
        ]),
    ];
    const inventario = [
        ['Cliente', 'Proyecto', 'Reporte', 'Tipo', 'Aprobación', '%', 'Estado', 'Deadline'],
    ];
    groups.forEach((group) => {
        group.projects.forEach((project) => {
            project.reports.forEach((row) => {
                inventario.push([
                    group.client,
                    project.project,
                    row.name,
                    row.type,
                    row.approval,
                    row.pct,
                    row.status,
                    row.deadline,
                ]);
            });
        });
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(resumen), 'Resumen');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(inventario), 'Inventario');
    XLSX.writeFile(wb, filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}
